import {
  WorkflowStepUserResponses,
  type DocumentDetailsVersion,
  type WorkflowDetails,
  type WorkflowStep,
} from "~/api/types";
import { DocumentVersionStatus } from "~/config/types";

type VersionWithStatus = Pick<DocumentDetailsVersion, "status">;
type VersionWithPermissions = Pick<DocumentDetailsVersion, "permissions">;
type VersionWithStatusAndPermissions = VersionWithStatus &
  VersionWithPermissions;

interface WorkflowStepControlOptions {
  canManage: boolean;
  canPublish: boolean;
  isWorkflowStarted: boolean;
  isActiveStep: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  versionStatus: DocumentVersionStatus;
}

export const isDraftVersion = (version: VersionWithStatus): boolean =>
  version.status === DocumentVersionStatus.draft;

export const isPublishedVersion = (version: VersionWithStatus): boolean =>
  version.status === DocumentVersionStatus.published;

const isArchivedVersion = (version: VersionWithStatus): boolean =>
  version.status === DocumentVersionStatus.archived;

const isDocumentArchived = (documentArchivedAt: string | null): boolean =>
  documentArchivedAt !== null;

export const isDocumentOrVersionArchived = (
  version: VersionWithStatus,
  documentArchivedAt: string | null,
): boolean =>
  isDocumentArchived(documentArchivedAt) || isArchivedVersion(version);

export const canManageVersion = (version: VersionWithPermissions): boolean =>
  version.permissions.canManage;

export const canPublishVersion = (version: VersionWithPermissions): boolean =>
  version.permissions.canPublish;

export const canEditDocumentContent = (
  version: VersionWithStatusAndPermissions,
  documentArchivedAt: string | null,
): boolean =>
  canManageVersion(version) &&
  !isDocumentOrVersionArchived(version, documentArchivedAt);

export const canEditDocumentMetadata = (
  version: VersionWithStatusAndPermissions,
  documentArchivedAt: string | null,
): boolean =>
  canManageVersion(version) &&
  !isDocumentOrVersionArchived(version, documentArchivedAt);

export const canArchiveDocument = (
  version: VersionWithPermissions,
  documentArchivedAt: string | null,
): boolean =>
  canManageVersion(version) && !isDocumentArchived(documentArchivedAt);

export const canEditWorkflow = (
  version: VersionWithStatusAndPermissions,
): boolean => canManageVersion(version) && isDraftVersion(version);

export const canSetWorkflowAdoptionDate = (
  version: VersionWithStatusAndPermissions,
  isLastStepActive: boolean,
): boolean =>
  canManageVersion(version) && (!isDraftVersion(version) || isLastStepActive);

export const canInviteReaders = (
  version: VersionWithStatusAndPermissions,
): boolean => canManageVersion(version) && !isArchivedVersion(version);

export const canSeeWorkflowTab = (
  userId: number,
  version: VersionWithPermissions,
  workflow?: WorkflowDetails,
): boolean =>
  workflow?.steps.some((step) =>
    step.users.some((u) => u.userId === userId),
  ) === true ||
  canManageVersion(version) ||
  canPublishVersion(version);

export const canSeeAnchoringTab = (
  version: VersionWithStatusAndPermissions,
): boolean => canManageVersion(version) && !isDraftVersion(version);

export const getApprovalStatus = (step: WorkflowStep) => {
  const totalCount = step.users.length;
  const approvedCount = step.users.filter(
    (u) => u.responseStatus === WorkflowStepUserResponses.approved,
  ).length;
  const isWaitingForApprovals =
    step.type === "approval" && approvedCount !== totalCount;

  return { isWaitingForApprovals, approvedCount, totalCount };
};

export const getWorkflowStepControlPermissions = ({
  canManage,
  canPublish,
  isWorkflowStarted,
  isActiveStep,
  isFirstStep,
  isLastStep,
  versionStatus,
}: WorkflowStepControlOptions) => {
  const canStartWorkflow = canManage && !isWorkflowStarted && isFirstStep;
  const canMoveBackward =
    canManage && isActiveStep && !isFirstStep && isWorkflowStarted;
  const canMoveForward =
    canManage && isActiveStep && !isLastStep && isWorkflowStarted;
  const showPublishButton = canPublish && isActiveStep && isLastStep;

  const showControls =
    (canStartWorkflow || isActiveStep) &&
    versionStatus === DocumentVersionStatus.draft;

  return {
    canStartWorkflow,
    canMoveBackward,
    canMoveForward,
    showPublishButton,
    showControls,
  };
};
